"use client";

import React, { useState, useMemo } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import InvestmentInquiryForm from "@/components/investment/InvestmentInquiryForm";
import {
	FaCheck,
	FaChartLine,
	FaUsers,
	FaBriefcase,
	FaShieldAlt,
	FaChartPie,
	FaSignOutAlt,
	FaMapMarkerAlt,
	FaMoneyBillAlt,
} from "react-icons/fa";
import type { IconType } from "react-icons";

type StrategyKey = "buy" | "optimize" | "scale" | "exit";

interface StrategyItem {
	title: string;
	time: string;
	icon: IconType;
	color: string;
	content: string;
	points: string[];
}

export default function InvestmentPageContent() {
	const t = useTranslations("InvestmentPage");
	const [activeTab, setActiveTab] = useState<StrategyKey>("buy");
	const [checklist, setChecklist] = useState<Record<number, boolean>>({
		1: false,
		2: false,
		3: false,
		4: false,
		5: false,
	});

	const handleCheck = (id: number) => {
		setChecklist((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const strategies: Record<StrategyKey, StrategyItem> = useMemo(
		() => ({
			buy: {
				title: t("phase1Title"),
				time: t("phase1Time"),
				icon: FaBriefcase,
				color: "bg-amber-500",
				content: t("phase1Content"),
				points: [t("phase1Point1"), t("phase1Point2"), t("phase1Point3")],
			},
			optimize: {
				title: t("phase2Title"),
				time: t("phase2Time"),
				icon: FaShieldAlt,
				color: "bg-blue-600",
				content: t("phase2Content"),
				points: [t("phase2Point1"), t("phase2Point2"), t("phase2Point3")],
			},
			scale: {
				title: t("phase3Title"),
				time: t("phase3Time"),
				icon: FaChartLine,
				color: "bg-purple-600",
				content: t("phase3Content"),
				points: [t("phase3Point1"), t("phase3Point2"), t("phase3Point3")],
			},
			exit: {
				title: t("phase4Title"),
				time: t("phase4Time"),
				icon: FaSignOutAlt,
				color: "bg-emerald-500",
				content: t("phase4Content"),
				points: [t("phase4Point1"), t("phase4Point2"), t("phase4Point3")],
			},
		}),
		[t]
	);

	const thesisCards = useMemo(
		() => [
			{ icon: FaBriefcase, title: t("thesis1Title"), text: t("thesis1Text") },
			{ icon: FaChartLine, title: t("thesis2Title"), text: t("thesis2Text") },
			{ icon: FaSignOutAlt, title: t("thesis3Title"), text: t("thesis3Text") },
		],
		[t]
	);

	const checklistItems = useMemo(
		() =>
			(
				[
					"criteria1",
					"criteria2",
					"criteria3",
					"criteria4",
					"criteria5",
				] as const
			).map((key, index) => ({ id: index + 1, text: t(key) })),
		[t]
	);

	const founders = useMemo(
		() => [
			{
				name: t("founder1Name"),
				role: t("founder1Role"),
				desc: t("founder1Desc"),
				skills: [t("founder1Skill1"), t("founder1Skill2"), t("founder1Skill3")],
				image: "/assets/profile/paul_poterasi152px.webp",
			},
			{
				name: t("founder2Name"),
				role: t("founder2Role"),
				desc: t("founder2Desc"),
				skills: [t("founder2Skill1"), t("founder2Skill2"), t("founder2Skill3")],
				image: "/assets/profile/dragos_leonte152px.webp",
			},
		],
		[t]
	);

	const checkedCount = Object.values(checklist).filter(Boolean).length;
	const progress = (checkedCount / 5) * 100;
	const currentStrategy = strategies[activeTab];
	const checkedCriteriaTexts = checklistItems
		.filter((item) => checklist[item.id])
		.map((item) => item.text);

	return (
		<div className="font-sans text-slate-800 bg-slate-50 dark:bg-gray-900 dark:text-gray-100 animate-fade-in content-page-lg">
			{/* Hero Section - full width, homepage hero image + text */}
			<header className="relative w-full pt-24 pb-16 border-b border-slate-200 dark:border-gray-700 overflow-hidden">
				<div className="absolute inset-0 z-0">
					<Image
						src="/assets/vendors/laptop-computer-writing-working.webp"
						alt=""
						fill
						className="object-cover grayscale-[30%] opacity-20 dark:opacity-25"
						sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px"
						priority
					/>
					<div className="absolute inset-0 bg-white dark:bg-gray-800/95 z-[1]" />
				</div>
				<div className="relative z-10 w-full section-padding text-center md:text-left">
					<div className="flex flex-col md:flex-row items-center justify-between gap-12">
						<div className="md:w-1/2">
							<span className="inline-block py-1.5 px-4 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-[1.4rem] font-bold tracking-wide uppercase mb-6">
								{t("badge")}
							</span>
							<h1 className="text-[4rem] min-[600px]:text-[5rem] font-serif font-bold text-slate-900 dark:text-white mb-6 leading-tight">
								{t("headingLine1")} <br />
								<span className="text-blue-800 dark:text-blue-300">
									{t("headingLine2")}
								</span>
							</h1>
							<p className="text-[1.8rem] text-slate-600 dark:text-gray-300 mb-10 leading-relaxed max-w-xl">
								{t("heroParagraph")}
							</p>
							<div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
								<a
									href="#criteria"
									className="px-8 py-4 bg-blue-900 dark:bg-blue-700 text-white rounded-lg font-medium text-[1.9rem] hover:bg-blue-800 dark:hover:bg-blue-600 transition text-center shadow-lg"
								>
									{t("ctaCriteria")}
								</a>
								<a
									href="#strategy"
									className="px-8 py-4 bg-white dark:bg-gray-700 text-blue-900 dark:text-blue-200 rounded-lg font-medium text-[1.9rem] border-2 border-slate-200 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-600 transition text-center"
								>
									{t("ctaStrategy")}
								</a>
							</div>
						</div>

						<div className="md:w-1/2 mt-12 md:mt-0 flex justify-center">
							<div className="grid grid-cols-2 gap-6 w-full max-w-[52rem] opacity-90">
								<div className="bg-slate-50 dark:bg-gray-700 p-8 rounded-2xl border border-slate-200 dark:border-gray-600 shadow-sm text-center transition-all duration-300 hover:scale-105 hover:shadow-xl hover:-translate-y-1">
									<FaMoneyBillAlt className="w-10 h-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-3" />
									<div className="text-[2.4rem] font-bold text-slate-900 dark:text-white">
										1M - 2M €
									</div>
									<div className="text-[1.4rem] text-slate-500 dark:text-gray-400 uppercase font-bold mt-1">
										{t("statTurnover")}
									</div>
								</div>
								<div className="bg-blue-900 dark:bg-blue-800 p-8 rounded-2xl border border-blue-700 dark:border-blue-600 shadow-sm text-center text-white transition-all duration-300 hover:scale-105 hover:shadow-xl hover:-translate-y-1">
									<FaChartPie className="w-10 h-10 text-amber-400 mx-auto mb-3" />
									<div className="text-[2.4rem] font-bold">3x - 5x</div>
									<div className="text-[1.4rem] text-blue-200 uppercase font-bold mt-1">
										{t("statMultiple")}
									</div>
								</div>
								<div className="bg-slate-50 dark:bg-gray-700 p-8 rounded-2xl border border-slate-200 dark:border-gray-600 shadow-sm text-center transition-all duration-300 hover:scale-105 hover:shadow-xl hover:-translate-y-1">
									<FaUsers className="w-10 h-10 text-purple-600 dark:text-purple-400 mx-auto mb-3" />
									<div className="text-[2.4rem] font-bold text-slate-900 dark:text-white">
										51% +
									</div>
									<div className="text-[1.4rem] text-slate-500 dark:text-gray-400 uppercase font-bold mt-1">
										{t("statStake")}
									</div>
								</div>
								<div className="bg-slate-50 dark:bg-gray-700 p-8 rounded-2xl border border-slate-200 dark:border-gray-600 shadow-sm text-center transition-all duration-300 hover:scale-105 hover:shadow-xl hover:-translate-y-1">
									<FaMapMarkerAlt className="w-10 h-10 text-amber-500 dark:text-amber-400 mx-auto mb-3" />
									<div className="text-[2.4rem] font-bold text-slate-900 dark:text-white">
										Max 2.5h
									</div>
									<div className="text-[1.4rem] text-slate-500 dark:text-gray-400 uppercase font-bold mt-1">
										{t("statDrive")}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</header>

			{/* Thesis / Philosophy Section - full width */}
			<section id="thesis" className="py-20 bg-white dark:bg-gray-800 w-full">
				<div className="w-full section-padding">
					<div className="text-center mb-16">
						<h2 className="text-blue-900 dark:text-blue-300 font-bold tracking-wide uppercase text-[1.4rem] mb-2">
							{t("thesisSubtitle")}
						</h2>
						<h3 className="text-[2.8rem] font-serif font-bold text-slate-900 dark:text-white">
							{t("thesisHeading")}
						</h3>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-10">
						{thesisCards.map((card, i) => {
							const Icon = card.icon;
							return (
								<div
									key={i}
									className="p-10 relative overflow-hidden bg-white dark:bg-gray-700 rounded-[1.5rem] border-2 border-[#ccf5ff] dark:border-blue-400/40 transition-all duration-300 hover:-translate-y-2 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-white dark:hover:bg-gray-600 hover:shadow-xl cursor-pointer group"
									style={{ boxShadow: "0 1rem 2.9rem 0 rgba(68,88,144,0.1)" }}
								>
									<div className="w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center text-blue-900 dark:text-blue-200 shadow-sm mb-8 group-hover:scale-110 transition-transform border border-slate-100 dark:border-gray-600">
										<Icon className="w-7 h-7" />
									</div>
									<h4 className="text-[2.2rem] font-bold mb-4 text-slate-900 dark:text-white">
										{card.title}
									</h4>
									<p className="text-[1.6rem] text-slate-600 dark:text-gray-300 leading-relaxed">
										{card.text}
									</p>
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{/* Strategy Section - full width */}
			<section
				id="strategy"
				className="py-20 bg-slate-900 text-white overflow-hidden w-full"
			>
				<div className="w-full section-padding">
					<div className="mb-12">
						<h2 className="text-amber-500 font-bold tracking-wide uppercase text-[1.4rem] mb-2">
							{t("strategySubtitle")}
						</h2>
						<h3 className="text-[2.8rem] font-serif font-bold">
							{t("strategyHeading")}
						</h3>
					</div>

					<div className="flex flex-col lg:flex-row gap-10">
						<div className="lg:w-1/3 flex flex-col space-y-3">
							{(Object.keys(strategies) as StrategyKey[]).map((key) => {
								const step = strategies[key];
								const isActive = activeTab === key;
								return (
									<button
										key={key}
										type="button"
										onClick={() => setActiveTab(key)}
										className={`text-left px-6 py-6 rounded-xl transition-all duration-300 border-l-4 ${
											isActive
												? "bg-blue-900/50 border-amber-500 shadow-lg translate-x-2"
												: "bg-slate-800 border-transparent hover:bg-slate-700 hover:translate-x-1"
										}`}
									>
										<div
											className={`font-bold text-[1.8rem] ${
												isActive ? "text-white" : "text-slate-300"
											}`}
										>
											{step.title}
										</div>
										<div className="text-[1.4rem] text-slate-400 font-mono mt-1">
											{step.time}
										</div>
									</button>
								);
							})}
						</div>

						<div className="lg:w-2/3 bg-slate-800 rounded-2xl p-10 border border-slate-700 relative min-h-[320px]">
							<div key={activeTab} className="animate-fade-in relative z-10">
								<div className="flex items-center mb-8">
									<div
										className={`p-4 rounded-full mr-5 ${currentStrategy.color}`}
									>
										<currentStrategy.icon className="w-8 h-8 text-white" />
									</div>
									<h3 className="text-[2.2rem] font-bold text-white">
										{currentStrategy.title}
									</h3>
								</div>
								<p className="text-slate-300 text-[1.8rem] leading-relaxed mb-10">
									{currentStrategy.content}
								</p>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
									{currentStrategy.points.map((point, idx) => (
										<div
											key={idx}
											className="flex items-start text-slate-400 p-4 bg-slate-700/30 rounded-lg text-[1.6rem]"
										>
											<FaCheck className="w-6 h-6 text-green-500 mr-4 mt-0.5 flex-shrink-0" />
											<span className="font-medium">{point}</span>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Checklist Section - full width */}
			<section id="criteria" className="py-20 bg-blue-900 text-white w-full">
				<div className="w-full section-padding">
					<div className="text-center mb-12">
						<h2 className="text-[2.8rem] font-serif font-bold">
							{t("criteriaTitle")}
						</h2>
						<p className="text-blue-200 mt-4 text-[1.8rem]">
							{t("criteriaSubtitle")}
						</p>
					</div>

					<div className="max-w-[96rem] mx-auto bg-white dark:bg-gray-800 rounded-2xl p-10 shadow-2xl text-slate-800 dark:text-gray-200">
						<div className="space-y-5">
							{checklistItems.map((item) => (
								<button
									key={item.id}
									type="button"
									onClick={() => handleCheck(item.id)}
									className={`flex w-full items-center p-5 rounded-xl cursor-pointer border-2 transition-all duration-200 text-left text-[1.8rem] ${
										checklist[item.id]
											? "bg-blue-50 dark:bg-blue-900/20 border-blue-500 shadow-inner"
											: "bg-slate-50 dark:bg-gray-700 border-slate-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500"
									}`}
									aria-pressed={checklist[item.id]}
									aria-label={`Toggle: ${item.text}`}
								>
									<div
										className={`w-8 h-8 rounded border-2 flex items-center justify-center mr-5 transition-colors flex-shrink-0 ${
											checklist[item.id]
												? "bg-blue-600 border-blue-600"
												: "bg-white dark:bg-gray-600 border-slate-300 dark:border-gray-500"
										}`}
									>
										{checklist[item.id] && (
											<FaCheck className="w-5 h-5 text-white" />
										)}
									</div>
									<span className="font-medium text-slate-700 dark:text-gray-200">
										{item.text}
									</span>
								</button>
							))}
						</div>

						<div className="mt-10 pt-8 border-t-2 border-slate-100 dark:border-gray-600">
							<div className="text-center font-bold text-[1.8rem] mb-5">
								{progress === 0 && (
									<span className="text-slate-400 dark:text-gray-500">
										{t("criteriaSelect")}
									</span>
								)}
								{progress > 0 && progress < 100 && (
									<span className="text-amber-500">{t("criteriaPartial")}</span>
								)}
								{progress === 100 && (
									<span className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
										<FaCheck className="w-6 h-6 flex-shrink-0" />{" "}
										{t("criteriaMatch")}
									</span>
								)}
							</div>
							<div className="w-full bg-slate-200 dark:bg-gray-600 rounded-full h-4 overflow-hidden">
								<div
									className={`h-full transition-all duration-500 ease-out ${
										progress === 100 ? "bg-green-500" : "bg-blue-600"
									}`}
									style={{ width: `${progress}%` }}
								/>
							</div>
						</div>

						{progress === 100 && (
							<InvestmentInquiryForm criteriaChecked={checkedCriteriaTexts} />
						)}
					</div>
				</div>
			</section>

			{/* Founders Section - full width */}
			<section className="py-20 bg-white dark:bg-gray-800 border-t border-slate-100 dark:border-gray-700 w-full">
				<div className="w-full section-padding">
					<div className="text-center mb-14">
						<h2 className="text-[2.8rem] font-bold text-slate-900 dark:text-white font-serif">
							{t("foundersTitle")}
						</h2>
						<p className="text-slate-500 dark:text-gray-400 mt-4 text-[1.8rem]">
							{t("foundersSubtitle")}
						</p>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-14">
						{founders.map((founder, i) => (
							<div
								key={i}
								className="flex flex-col items-center text-center p-10 relative overflow-hidden bg-white dark:bg-gray-700 rounded-[1.5rem] border-2 border-[#ccf5ff] dark:border-blue-400/40 transition-all duration-300 hover:-translate-y-2 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-xl cursor-pointer"
								style={{ boxShadow: "0 1rem 2.9rem 0 rgba(68,88,144,0.1)" }}
							>
								<div className="relative w-[15.2rem] h-[15.2rem] rounded-full overflow-hidden mx-auto mb-8">
									<Image
										src={founder.image}
										alt={founder.name}
										fill
										sizes="152px"
										className="object-cover"
									/>
								</div>
								<h3 className="text-[2.2rem] font-bold text-slate-900 dark:text-white">
									{founder.name}
								</h3>
								<div className="text-blue-700 dark:text-blue-300 font-medium mb-5 text-[1.8rem]">
									{founder.role}
								</div>
								<p className="text-slate-600 dark:text-gray-300 text-[1.6rem] mb-8 px-6 leading-relaxed">
									{founder.desc}
								</p>
								<div className="flex flex-wrap justify-center gap-3">
									{founder.skills.map((skill) => (
										<span
											key={skill}
											className="px-4 py-2 bg-white dark:bg-gray-600 border border-slate-200 dark:border-gray-500 rounded-full text-[1.4rem] text-slate-500 dark:text-gray-300 font-medium"
										>
											{skill}
										</span>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</div>
	);
}
